import React, { useState, useCallback } from "react";
import { RgbaColorPicker } from "react-colorful";
import { Box, Button, Popover, IconButton, Tooltip } from "@material-ui/core";
import ColorLensIcon from "@material-ui/icons/ColorLens";
import PipetteIcon from "@material-ui/icons/Opacity";

const parseRgba = (str) => {
	const m = str && str.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
	if (m) return { r: +m[1], g: +m[2], b: +m[3], a: m[4] !== undefined ? +m[4] : 1 };
	return { r: 100, g: 100, b: 255, a: 0.8 };
};

const toRgba = (r, g, b, a) =>
	`rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${a})`;

const hexToRgba = (hex) => {
	hex = hex.replace(/^#/, "");
	if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
	const r = parseInt(hex.slice(0, 2), 16);
	const g = parseInt(hex.slice(2, 4), 16);
	const b = parseInt(hex.slice(4, 6), 16);
	return { r, g, b, a: 1 };
};

const hasEyeDropper = typeof window !== "undefined" && "EyeDropper" in window;

export default function RgbaColorPickerPopover({ value, onChange, label }) {
	const [anchorEl, setAnchorEl] = useState(null);
	const rgba = parseRgba(value);

	const handleOpen = (e) => setAnchorEl(e.currentTarget);
	const handleClose = () => setAnchorEl(null);

	const handleChange = useCallback(
		(col) => onChange(toRgba(col.r, col.g, col.b, col.a)),
		[onChange]
	);

	const handleEyeDropper = useCallback(async () => {
		if (!hasEyeDropper) return;
		try {
			const dropper = new window.EyeDropper();
			const result = await dropper.open();
			if (result?.sRGBHex) {
				const col = hexToRgba(result.sRGBHex);
				onChange(toRgba(col.r, col.g, col.b, 1));
			}
		} catch (e) {
			// User cancelled or API failed
		}
	}, [onChange]);

	return (
		<Box>
			{label && (
				<Box fontSize={12} fontWeight="bold" mb={0.5}>
					{label}
				</Box>
			)}
			<Box display="flex" alignItems="center">
				<Box
					onClick={handleOpen}
					title="Abrir selector de color"
					style={{
						backgroundImage: `url("data:image/svg+xml,%3Csvg width='16' height='16' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h8v8H0zm8 8h8v8H8z' fill='%23ddd'/%3E%3C/svg%3E")`,
						border: "2px solid #999",
						borderRadius: 8,
						minWidth: 48,
						height: 36,
						cursor: "pointer",
						flexShrink: 0,
						overflow: "hidden",
					}}
				>
					<Box
						style={{
							width: "100%",
							height: "100%",
							backgroundColor: value || "rgba(100,100,255,0.8)",
						}}
					/>
				</Box>
				{hasEyeDropper && (
					<Tooltip title="Cuentagotas: seleccionar color de la pantalla">
						<IconButton size="small" onClick={handleEyeDropper} color="primary" style={{ marginLeft: 4 }}>
							<PipetteIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				)}
			</Box>
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
				PaperProps={{ style: { borderRadius: 12, overflow: "visible" } }}
			>
				<Box p={2} display="flex" flexDirection="column" alignItems="center">
					<Box
						style={{
							height: 220,
							minWidth: 220,
							borderRadius: 10,
							overflow: "visible",
							display: "flex",
							flexDirection: "column",
							alignItems: "stretch",
						}}
					>
						<RgbaColorPicker color={rgba} onChange={handleChange} style={{ width: "100%", height: "100%" }} />
					</Box>
					{hasEyeDropper && (
						<Button
							size="small"
							startIcon={<ColorLensIcon />}
							onClick={() => {
								handleEyeDropper();
								handleClose();
							}}
							style={{ marginTop: 12 }}
						>
							Cuentagotas
						</Button>
					)}
				</Box>
			</Popover>
		</Box>
	);
}
